// Fase 1+2 av litteratur-oppgradering (task #349-360, 2026-07-07)
// Erstatter dagens "AI-forslag"-modal med resultater fra live-søk mot
// NDLA + Nasjonalbiblioteket + Crossref+Unpaywall. Viser lisens-badge
// per treff og krever rights-bekreftelse for kommersiell/bokhylla.
import React, { useState } from 'react';
import {
  X, Search, Loader2, ExternalLink, BookOpen,
  ShieldCheck, ShieldAlert, ShieldQuestion, Lock, Info, Check,
} from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

interface Suggestion {
  title: string;
  author?: string;
  publisher?: string;
  description?: string;
  url: string;
  external_id: string;
  literature_type: string;
  language?: string;
  publication_year?: string;
  isbn?: string;
  doi?: string;
  discovery_source: string;
  discovery_query?: string;
  license_category: 'free' | 'open_access' | 'bokhylla' | 'commercial' | 'unknown' | 'no_digital_access';
  license_label: string;
  license_confidence: number;
  license_source_url?: string;
  has_full_content: boolean;
  content_url?: string;
  relevance_score?: number;
  relevance_reason?: string;
  already_added?: boolean;
  is_collection?: boolean; // NDLA subject/topic — samleside
  subject_name?: string;
}

interface NdlaSubject {
  id: string;
  name: string;
  url: string;
}

interface NdlaTopic {
  id: string;
  name: string;
  url: string;
}

type ModalMode = 'ai' | 'freetext' | 'ndla-taxonomy';

// Task #349-360 (2026-07-07): cache siste søkeresultat per plan i sessionStorage.
// Overlever modal-close/open, men ikke sesjon-lukking. Gir mulighet for gjentatt
// tilgang uten nytt KI-kall. Bruker manuelt "Nytt søk"-knapp for friske spørringer.
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min
const cacheKey = (planId: string) => `lit-discovery:${planId}`;

interface CachedSearch {
  suggestions: Suggestion[];
  meta: any;
  timestamp: number;
}

function loadCache(planId: string): CachedSearch | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(planId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSearch;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function saveCache(planId: string, suggestions: Suggestion[], meta: any) {
  try {
    sessionStorage.setItem(cacheKey(planId), JSON.stringify({ suggestions, meta, timestamp: Date.now() }));
  } catch (e) {
    // Ignorer quota errors
  }
}

function clearCache(planId: string) {
  try { sessionStorage.removeItem(cacheKey(planId)); } catch (e) {}
}

interface LiteratureDiscoveryModalProps {
  studyPlanId: string;
  isOpen: boolean;
  onClose: () => void;
  // Task #383: onLiteratureAdded får ny updated_at fra backend så foreldren
  // kan oppdatere sin dbUpdatedAt-state og unngå falsk 409-konflikt.
  onLiteratureAdded: (opts?: { updated_at?: string | null }) => void;
}

const LICENSE_STYLE: Record<string, {
  bg: string; text: string; border: string; icon: any; short: string; explainer: string;
}> = {
  free: {
    bg: 'bg-green-50', text: 'text-green-800', border: 'border-green-300',
    icon: ShieldCheck, short: 'Fri lisens',
    explainer: 'Innholdet kan lastes ned og lagres lokalt lovlig (CC-lisens, NLOD, eller public domain).',
  },
  open_access: {
    bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-300',
    icon: ShieldCheck, short: 'Åpen tilgang',
    explainer: 'Vitenskapelig åpen tilgang-versjon (grønn/gull OA) — kan snapshots til bruk i systemet.',
  },
  bokhylla: {
    bg: 'bg-kf-blue-50', text: 'text-kf-blue-800', border: 'border-kf-blue-300',
    icon: BookOpen, short: 'Bokhylla',
    explainer: 'Fritt lesbart fra Norge via nb.no, men kan ikke kopieres til systemet. Krever bekreftelse.',
  },
  commercial: {
    bg: 'bg-kf-orange-50', text: 'text-kf-orange-900', border: 'border-kf-orange-300',
    icon: ShieldAlert, short: 'Krever avtale',
    explainer: 'Opphavsrettslig beskyttet. Krever gyldig Kopinor-avtale eller egen tillatelse.',
  },
  unknown: {
    bg: 'bg-gray-50', text: 'text-gray-800', border: 'border-gray-300',
    icon: ShieldQuestion, short: 'Ukjent',
    explainer: 'Lisens ikke klart identifisert. Krever manuell vurdering av medlemsorg.',
  },
  no_digital_access: {
    bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-300',
    icon: Lock, short: 'Ingen digital tilgang',
    explainer: 'Kun tilgjengelig i NBs lokaler.',
  },
};

const SOURCE_LABEL: Record<string, string> = {
  ndla: 'NDLA',
  nb: 'Nasjonalbiblioteket',
  crossref: 'Crossref/DOI',
  curated: 'Kuratert kilde',
  manual: 'Manuelt tillagt',
};

export default function LiteratureDiscoveryModal({
  studyPlanId, isOpen, onClose, onLiteratureAdded,
}: LiteratureDiscoveryModalProps) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [error, setError] = useState('');
  const [pendingAccept, setPendingAccept] = useState<Suggestion | null>(null);
  const [confirmationBasis, setConfirmationBasis] = useState<string>('kopinor');
  const [confirmationRef, setConfirmationRef] = useState('');
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [cacheAge, setCacheAge] = useState<number | null>(null);

  // Fase 4F (task #384): coverage-rapport når samleside legges til
  const [coverageReport, setCoverageReport] = useState<{
    collectionTitle: string;
    covered: Array<{ link_id: string; title: string; external_id: string; url?: string }>;
    notCovered: Array<{ link_id: string; title: string; external_id: string }>;
    collectionTotalArticles: number;
  } | null>(null);
  const [removingLinkIds, setRemovingLinkIds] = useState<Set<string>>(new Set());

  // Fase 4B/C (task #370-371): modus + fritekst/taksonomi-state
  const [mode, setMode] = useState<ModalMode>('ai');
  const [freeTextQuery, setFreeTextQuery] = useState('');
  const [freeTextSources, setFreeTextSources] = useState<{ ndla: boolean; nb: boolean; crossref: boolean }>({
    ndla: true, nb: true, crossref: true,
  });
  const [ndlaSubjects, setNdlaSubjects] = useState<NdlaSubject[]>([]);
  const [ndlaSubjectQuery, setNdlaSubjectQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<NdlaSubject | null>(null);
  const [ndlaTopics, setNdlaTopics] = useState<NdlaTopic[]>([]);
  const [loadingTaxonomy, setLoadingTaxonomy] = useState(false);

  // Ved åpning: last cache hvis finnes, deretter oppdater already_added-status
  // fra backend i tilfelle brukeren har lagt til noe utenfor cachen (f.eks.
  // manuelt, eller i tidligere sesjon).
  React.useEffect(() => {
    if (!isOpen || !studyPlanId) return;
    const cached = loadCache(studyPlanId);
    if (cached) {
      setSuggestions(cached.suggestions);
      setMeta(cached.meta);
      setCacheAge(Date.now() - cached.timestamp);

      // Task #349-360: friskt oppslag mot backend for å oppdatere
      // already_added-status uten å måtte kjøre nytt LLM-søk.
      authenticatedFetch(`/api/literature/existing-external-ids/${studyPlanId}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && Array.isArray(data.external_ids)) {
            const existing = new Set<string>(data.external_ids);
            const updated = cached.suggestions.map(s => ({
              ...s,
              already_added: existing.has(s.external_id),
            }));
            setSuggestions(updated);
            saveCache(studyPlanId, updated, cached.meta);
          }
        })
        .catch(() => {/* ignorer, cache er brukelig som fallback */});
    }
  }, [isOpen, studyPlanId]);

  const runFreeText = async () => {
    if (!freeTextQuery.trim()) return;
    setLoading(true);
    setError('');
    setSuggestions([]);
    try {
      const sources: string[] = [];
      if (freeTextSources.ndla) sources.push('ndla');
      if (freeTextSources.nb) sources.push('nb');
      if (freeTextSources.crossref) sources.push('crossref');
      if (sources.length === 0) {
        setError('Velg minst én kilde å søke i');
        return;
      }
      const res = await authenticatedFetch('/api/literature/free-text-search', {
        method: 'POST',
        body: JSON.stringify({
          query: freeTextQuery.trim(),
          sources,
          study_plan_id: studyPlanId,
          limit: 12,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSuggestions(data.suggestions || []);
        setMeta({ ...data.meta, mode: 'freetext' });
      } else {
        setError(data.error || 'Kunne ikke søke');
      }
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    } finally {
      setLoading(false);
    }
  };

  const loadNdlaSubjects = async (q?: string) => {
    setLoadingTaxonomy(true);
    try {
      const url = q ? `/api/literature/ndla/subjects?q=${encodeURIComponent(q)}` : '/api/literature/ndla/subjects';
      const res = await authenticatedFetch(url);
      const data = await res.json();
      if (data.success) setNdlaSubjects(data.subjects || []);
    } catch (e) { /* stille feil */ }
    finally { setLoadingTaxonomy(false); }
  };

  const loadNdlaTopics = async (subjectId: string) => {
    setLoadingTaxonomy(true);
    try {
      const res = await authenticatedFetch(`/api/literature/ndla/topics?subject_id=${encodeURIComponent(subjectId)}`);
      const data = await res.json();
      if (data.success) setNdlaTopics(data.topics || []);
    } catch (e) { /* stille feil */ }
    finally { setLoadingTaxonomy(false); }
  };

  const addNdlaCollection = async (item: { id: string; name: string; url: string }, type: 'subject' | 'topic') => {
    const suggestion: Suggestion = {
      title: item.name,
      author: '',
      publisher: 'NDLA',
      description: type === 'subject'
        ? `Fagoversikt for ${item.name} med alle emner og læringsressurser.`
        : `Emneoversikt: ${item.name}.`,
      url: item.url,
      external_id: type === 'subject'
        ? `ndla-subject-${item.id.replace('urn:subject:', '')}`
        : `ndla-topic-${item.id.replace('urn:topic:', '')}`,
      literature_type: 'website',
      language: 'nb',
      discovery_source: 'ndla',
      license_category: 'free',
      license_label: 'CC BY-SA 4.0',
      license_confidence: 1.0,
      license_source_url: 'https://creativecommons.org/licenses/by-sa/4.0/deed.no',
      has_full_content: false,
      is_collection: true,
    };
    await acceptSuggestion(suggestion);
  };

  const runSearch = async () => {
    setLoading(true);
    setError('');
    setSuggestions([]);
    try {
      const res = await authenticatedFetch('/api/literature/discover', {
        method: 'POST',
        body: JSON.stringify({ study_plan_id: studyPlanId, count: 12 }),
      });
      const data = await res.json();
      if (data.success) {
        const list = data.suggestions || [];
        setSuggestions(list);
        setMeta(data.meta);
        setCacheAge(0);
        saveCache(studyPlanId, list, data.meta);
      } else {
        setError(data.error || 'Kunne ikke søke etter litteratur');
      }
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    } finally {
      setLoading(false);
    }
  };

  const acceptSuggestion = async (suggestion: Suggestion, withConfirmation?: {
    basis: string; agreement_reference?: string;
  }) => {
    try {
      const res = await authenticatedFetch('/api/literature/discover/accept', {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: studyPlanId,
          suggestion,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || 'Kunne ikke legge til litteratur');
        return;
      }

      // Hvis lisens krever bekreftelse, registrer den også nå
      if (withConfirmation && data.link_id) {
        await authenticatedFetch('/api/literature/rights-confirmation', {
          method: 'POST',
          body: JSON.stringify({
            literature_link_id: data.link_id,
            basis: withConfirmation.basis,
            agreement_reference: withConfirmation.agreement_reference,
          }),
        });
      }

      setAdded(prev => new Set(prev).add(suggestion.external_id));

      // Oppdater cache slik at neste åpning av modal-en viser at kilden
      // er lagt til (ellers propageres ikke `already_added` gjennom cachen)
      const updated = suggestions.map(s =>
        s.external_id === suggestion.external_id ? { ...s, already_added: true } : s
      );
      setSuggestions(updated);
      saveCache(studyPlanId, updated, meta);

      // Videresend ny updated_at til foreldren så dbUpdatedAt kan oppdateres
      onLiteratureAdded({ updated_at: data.updated_at || null });

      // Fase 4F: hvis backend returnerte coverage-rapport (kun for NDLA-samlesider),
      // vis konkret dekker-status til brukeren.
      if (data.coverage && data.coverage.covered_count > 0) {
        setCoverageReport({
          collectionTitle: suggestion.title,
          covered: data.coverage.covered_articles || [],
          notCovered: data.coverage.not_covered_articles || [],
          collectionTotalArticles: data.coverage.collection_total_articles || 0,
        });
      }
    } catch (e: any) {
      setError(e.message || 'Feil ved lagring');
    }
  };

  const removeCoveredArticle = async (linkId: string) => {
    setRemovingLinkIds(prev => new Set(prev).add(linkId));
    try {
      // Bruker eksisterende DELETE-endepunkt som håndterer både gammel og
      // ny modell (literature-routes.js:568 — prøver linked hvis manual mangler)
      const res = await authenticatedFetch(
        `/api/study-plans/${studyPlanId}/literature/${linkId}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (data.success || res.ok) {
        setCoverageReport(prev => prev ? {
          ...prev,
          covered: prev.covered.filter(a => a.link_id !== linkId),
        } : null);
        onLiteratureAdded({ updated_at: data.updated_at || null });
      }
    } catch (e) {
      console.error('remove failed', e);
    } finally {
      setRemovingLinkIds(prev => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });
    }
  };

  const handleAcceptClick = (s: Suggestion) => {
    const requiresConfirmation = ['commercial', 'bokhylla', 'unknown'].includes(s.license_category);
    if (requiresConfirmation) {
      setPendingAccept(s);
      setConfirmationBasis(s.license_category === 'bokhylla' ? 'public_reading_only' : 'kopinor');
      setConfirmationRef('');
    } else {
      acceptSuggestion(s);
    }
  };

  const confirmAndAccept = () => {
    if (!pendingAccept) return;
    acceptSuggestion(pendingAccept, {
      basis: confirmationBasis,
      agreement_reference: confirmationRef,
    });
    setPendingAccept(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-kf-light">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2 text-kf-primary">
              <Search className="text-kf-primary" size={22} />
              Søk etter litteratur
            </h2>
            <p className="text-sm text-kf-primary-80 mt-1">
              Live-søk mot NDLA, Nasjonalbiblioteket og Crossref/DOI. Lisens vises per treff.
            </p>
          </div>
          <button onClick={onClose} className="text-kf-primary-60 hover:text-kf-primary">
            <X size={24} />
          </button>
        </div>

        {/* Fase 4B/C: modus-tabs */}
        <div className="flex border-b bg-white px-6">
          {[
            { key: 'ai' as ModalMode, label: 'KI-forslag', desc: 'Automatisk søk basert på studieplan' },
            { key: 'freetext' as ModalMode, label: 'Fritekst-søk', desc: 'Søk direkte med egen tekst' },
            { key: 'ndla-taxonomy' as ModalMode, label: 'NDLA-fagoversikt', desc: 'Velg fag/emne fra NDLA' },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setMode(t.key);
                setError('');
                if (t.key === 'ndla-taxonomy' && ndlaSubjects.length === 0) loadNdlaSubjects();
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                mode === t.key
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-kf-primary-60 hover:text-kf-primary'
              }`}
              title={t.desc}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {/* Fase 4F: dekker-rapport når samleside legges til */}
          {coverageReport && (
            <div className="mb-4 bg-kf-accent-10 border border-kf-accent rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <BookOpen size={20} className="text-kf-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-kf-primary mb-1">
                    Samlesiden "{coverageReport.collectionTitle}" er lagt til
                  </div>
                  <div className="text-sm text-kf-primary-80">
                    Samlesiden dekker <strong>{coverageReport.collectionTotalArticles} artikler</strong> hos NDLA.
                    Vi har verifisert direkte mot NDLA at <strong>{coverageReport.covered.length} av dine eksisterende artikler</strong> ligger under denne samlesiden.
                  </div>
                </div>
                <button
                  onClick={() => setCoverageReport(null)}
                  className="text-kf-primary-60 hover:text-kf-primary flex-shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              {coverageReport.covered.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-medium text-kf-primary mb-2">
                    Disse artiklene dekkes allerede av samlesiden — kan fjernes:
                  </div>
                  <div className="space-y-1">
                    {coverageReport.covered.map(a => (
                      <div key={a.link_id} className="flex items-center justify-between bg-white rounded p-2">
                        <div className="text-sm text-kf-primary flex-1">{a.title}</div>
                        <button
                          onClick={() => removeCoveredArticle(a.link_id)}
                          disabled={removingLinkIds.has(a.link_id)}
                          className="text-xs px-3 py-1 rounded bg-kf-primary text-white hover:bg-kf-dark disabled:opacity-50"
                        >
                          {removingLinkIds.has(a.link_id) ? 'Fjerner...' : 'Fjern'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {coverageReport.notCovered.length > 0 && (
                <div className="mt-3 text-sm text-kf-primary-70">
                  <strong>{coverageReport.notCovered.length}</strong> andre NDLA-artikler i studieplanen ligger IKKE under denne samlesiden — de forblir uendret.
                </div>
              )}
            </div>
          )}

          {/* Modus-spesifikk topp-linje */}
          {mode === 'freetext' && (
            <div className="mb-4 bg-kf-light border border-kf-primary-20 rounded p-4">
              <label className="block text-sm font-medium text-kf-primary mb-2">Søketekst</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={freeTextQuery}
                  onChange={e => setFreeTextQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') runFreeText(); }}
                  placeholder="F.eks. motiverende samtale, ISBN 9788245012345, DOI 10.xxxx/xxxx"
                  className="flex-1 border border-kf-primary-40 rounded p-2 focus:border-kf-primary focus:outline-none"
                />
                <button
                  onClick={runFreeText}
                  disabled={loading || !freeTextQuery.trim()}
                  className="bg-kf-primary text-white px-4 py-2 rounded hover:bg-kf-dark disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Search size={16} />
                  Søk
                </button>
              </div>
              <div className="flex gap-4 text-sm text-kf-primary">
                <span className="font-medium">Søk i:</span>
                {(['ndla', 'nb', 'crossref'] as const).map(src => (
                  <label key={src} className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={freeTextSources[src]}
                      onChange={e => setFreeTextSources(prev => ({ ...prev, [src]: e.target.checked }))}
                    />
                    {SOURCE_LABEL[src]}
                  </label>
                ))}
              </div>
              <p className="text-xs text-kf-primary-60 mt-2">
                Gratis søk uten KI-kostnad. Lisens hentes fra kildens metadata.
              </p>
            </div>
          )}

          {mode === 'ndla-taxonomy' && !selectedSubject && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-kf-primary mb-2">Velg NDLA-fag</label>
              <input
                type="text"
                value={ndlaSubjectQuery}
                onChange={e => {
                  setNdlaSubjectQuery(e.target.value);
                  loadNdlaSubjects(e.target.value);
                }}
                placeholder="Filtrer fag (f.eks. helse, matte, norsk)"
                className="w-full border border-kf-primary-40 rounded p-2 mb-3 focus:border-kf-primary focus:outline-none"
              />
              {loadingTaxonomy && <Loader2 className="animate-spin text-kf-primary mx-auto my-4" size={24} />}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                {ndlaSubjects.map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSelectedSubject(s);
                      loadNdlaTopics(s.id);
                    }}
                    className="text-left border border-kf-primary-20 hover:border-kf-primary rounded p-3 hover:bg-kf-light transition-colors"
                  >
                    <div className="font-medium text-kf-primary">{s.name}</div>
                    <div className="text-xs text-kf-primary-60 mt-1">Klikk for å se emner og legge til</div>
                  </button>
                ))}
                {!loadingTaxonomy && ndlaSubjects.length === 0 && (
                  <div className="text-center text-kf-primary-60 py-4 col-span-2">Ingen fag matcher søket</div>
                )}
              </div>
            </div>
          )}

          {mode === 'ndla-taxonomy' && selectedSubject && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => { setSelectedSubject(null); setNdlaTopics([]); }}
                  className="text-kf-primary hover:text-kf-dark text-sm inline-flex items-center gap-1"
                >
                  ← Tilbake til fag
                </button>
              </div>
              <div className="bg-kf-light border border-kf-primary-20 rounded p-4 mb-4">
                <div className="text-lg font-semibold text-kf-primary mb-2">{selectedSubject.name}</div>
                <p className="text-sm text-kf-primary-80 mb-3">
                  Legg til HELE FAGET (én kobling som dekker alt innhold, oppdateres automatisk),
                  eller velg spesifikke emner nedenfor.
                </p>
                <button
                  onClick={() => addNdlaCollection(selectedSubject, 'subject')}
                  disabled={added.has(`ndla-subject-${selectedSubject.id.replace('urn:subject:', '')}`)}
                  className="bg-kf-primary text-white text-sm px-4 py-2 rounded hover:bg-kf-dark disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {added.has(`ndla-subject-${selectedSubject.id.replace('urn:subject:', '')}`)
                    ? <><Check size={14} /> Faget er lagt til</>
                    : <><BookOpen size={14} /> Legg til hele faget</>}
                </button>
              </div>
              <div className="text-sm font-medium text-kf-primary mb-2">Eller velg spesifikt emne:</div>
              {loadingTaxonomy && <Loader2 className="animate-spin text-kf-primary mx-auto my-4" size={24} />}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {ndlaTopics.map(t => {
                  const topicKey = `ndla-topic-${t.id.replace('urn:topic:', '')}`;
                  const isAdded = added.has(topicKey);
                  return (
                    <div key={t.id} className="flex items-center justify-between border border-kf-primary-20 rounded p-3">
                      <div className="text-sm text-kf-primary flex-1">{t.name}</div>
                      <button
                        onClick={() => addNdlaCollection(t, 'topic')}
                        disabled={isAdded}
                        className={`text-xs px-3 py-1 rounded ${
                          isAdded
                            ? 'bg-kf-success/10 text-kf-success border border-kf-success/40'
                            : 'bg-kf-primary text-white hover:bg-kf-dark'
                        }`}
                      >
                        {isAdded ? <><Check size={12} className="inline mr-1" /> Lagt til</> : 'Legg til'}
                      </button>
                    </div>
                  );
                })}
                {!loadingTaxonomy && ndlaTopics.length === 0 && (
                  <div className="text-center text-kf-primary-60 py-4">Ingen emner funnet under dette faget</div>
                )}
              </div>
            </div>
          )}

          {mode === 'ai' && suggestions.length === 0 && !loading && (
            <div className="text-center py-10">
              <BookOpen className="text-kf-primary-40 mx-auto mb-4" size={48} />
              <p className="text-kf-primary-80 mb-4">
                Start KI-søket for å finne relevant litteratur basert på studieplanen.
              </p>
              <button
                onClick={runSearch}
                className="bg-kf-primary text-white px-6 py-2 rounded-lg hover:bg-kf-dark inline-flex items-center gap-2"
              >
                <Search size={18} />
                Start KI-søk
              </button>
            </div>
          )}

          {loading && (
            <div className="text-center py-10">
              <Loader2 className="animate-spin text-kf-primary mx-auto mb-4" size={40} />
              <p className="text-kf-primary-80">
                {mode === 'freetext' ? 'Søker...' : 'Søker etter relevant litteratur...'}
              </p>
              <p className="text-sm text-kf-primary-60 mt-2">
                {mode === 'ai'
                  ? 'Genererer søkespørringer og kaller kildene parallelt. Tar 15–30 sekunder.'
                  : 'Kaller kildene parallelt.'}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-kf-error/10 border border-kf-error/40 rounded p-4 mb-4 text-kf-error text-sm">
              {error}
            </div>
          )}

          {mode !== 'ndla-taxonomy' && suggestions.length > 0 && (
            <>
              {meta && (
                <div className="bg-kf-light border border-kf-primary-20 rounded p-3 mb-4 text-sm text-kf-primary flex items-start gap-3">
                  <Info size={18} className="flex-shrink-0 mt-0.5 text-kf-primary" />
                  <div className="flex-1">
                    <div>
                      <strong>{suggestions.length}</strong> treff funnet — via {(meta.adapters_used || []).map((a: string) => SOURCE_LABEL[a] || a).join(', ')}.
                      {meta.queries && (
                        <> Søkespørringer: <em>{meta.queries.map((q: string) => `"${q}"`).join(', ')}</em>.</>
                      )}
                    </div>
                    {cacheAge !== null && cacheAge > 30000 && (
                      <div className="text-xs mt-1 text-kf-primary-80">
                        Vist fra lokal cache ({Math.round(cacheAge / 60000)} min siden). Bruk "Nytt søk" for friske treff.
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      clearCache(studyPlanId);
                      runSearch();
                    }}
                    className="flex-shrink-0 bg-kf-accent hover:bg-kf-accent-80 text-kf-primary text-xs font-medium px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                  >
                    <Search size={14} />
                    Nytt søk
                  </button>
                </div>
              )}

              <div className="space-y-3">
                {suggestions.map((s) => {
                  const style = LICENSE_STYLE[s.license_category] || LICENSE_STYLE.unknown;
                  const IconComponent = style.icon;
                  // "Lagt til" enten via denne sesjonen (added) eller
                  // fra tidligere (already_added fra backend).
                  const isAdded = added.has(s.external_id) || s.already_added === true;
                  return (
                    <div key={s.external_id} className={`border rounded-lg p-4 ${style.border} bg-white`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`}>
                              <IconComponent size={12} />
                              {style.short}
                            </span>
                            {s.is_collection && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-kf-accent-20 text-kf-primary border border-kf-accent-40">
                                <BookOpen size={12} />
                                Samleside — dekker flere artikler
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {SOURCE_LABEL[s.discovery_source] || s.discovery_source}
                            </span>
                            {s.publication_year && (
                              <span className="text-xs text-gray-500">{s.publication_year}</span>
                            )}
                            {s.literature_type && (
                              <span className="text-xs text-gray-500 capitalize">{s.literature_type}</span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                          {(s.author || s.publisher) && (
                            <p className="text-sm text-gray-600 mb-1">
                              {s.author && <span>{s.author}</span>}
                              {s.author && s.publisher && <span> · </span>}
                              {s.publisher && <em>{s.publisher}</em>}
                              {(s.isbn || s.doi) && <span> · {s.isbn ? `ISBN ${s.isbn}` : `DOI ${s.doi}`}</span>}
                            </p>
                          )}
                          {s.description && (
                            <p className="text-sm text-gray-700 mb-2 line-clamp-3">{s.description}</p>
                          )}
                          <div className={`text-xs ${style.text} italic mb-2`}>
                            {style.explainer}
                          </div>
                          {s.relevance_reason && (
                            <p className="text-xs text-kf-primary">
                              <strong>KI vurderer:</strong> {s.relevance_reason}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-kf-primary hover:text-kf-dark text-xs flex items-center gap-1 whitespace-nowrap"
                          >
                            <ExternalLink size={12} />
                            Åpne kilde
                          </a>
                          {isAdded ? (
                            <span className="inline-flex items-center gap-1 bg-kf-success/10 text-kf-success border border-kf-success/40 rounded px-2 py-1 text-xs">
                              <Check size={12} />
                              Lagt til
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAcceptClick(s)}
                              className="bg-kf-primary text-white text-xs px-3 py-1 rounded hover:bg-kf-dark whitespace-nowrap"
                            >
                              Legg til
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center justify-center border-t pt-4">
                <div className="text-xs text-kf-primary-60">
                  {added.size > 0 && <span className="text-kf-success font-medium">{added.size} lagt til nå</span>}
                  {added.size > 0 && ' · '}
                  Cachet lokalt så du kan komme tilbake uten nytt KI-kall
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rights confirmation modal */}
      {pendingAccept && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert className="text-kf-accent flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-kf-primary">Bekreft bruksrett</h3>
                <p className="text-sm text-kf-primary-80 mt-1">
                  «{pendingAccept.title}» krever bekreftelse fra organisasjonen din.
                </p>
              </div>
            </div>

            <div className="bg-kf-accent-20 border border-kf-accent-40 rounded p-3 mb-4 text-sm text-kf-primary">
              <strong>Lisens:</strong> {pendingAccept.license_label}<br/>
              {LICENSE_STYLE[pendingAccept.license_category]?.explainer}
            </div>

            <label className="block text-sm font-medium text-kf-primary mb-2">
              Grunnlag for bruken:
            </label>
            <select
              value={confirmationBasis}
              onChange={e => setConfirmationBasis(e.target.value)}
              className="w-full border border-kf-primary-40 rounded p-2 mb-3 focus:border-kf-primary focus:outline-none"
            >
              <option value="kopinor">Kopinor-avtale (vanligste)</option>
              <option value="own_agreement">Egen forlagsavtale</option>
              <option value="author_permission">Direkte tillatelse fra rettighetshaver</option>
              <option value="public_reading_only">Kun offentlig lesing (bokhylla-modus)</option>
              <option value="other">Annen hjemmel</option>
            </select>

            <label className="block text-sm font-medium text-kf-primary mb-2">
              Referanse (valgfritt):
            </label>
            <input
              type="text"
              value={confirmationRef}
              onChange={e => setConfirmationRef(e.target.value)}
              placeholder="Kopinor-medlemsnummer, avtale-navn e.l."
              className="w-full border border-kf-primary-40 rounded p-2 mb-4 focus:border-kf-primary focus:outline-none"
            />

            <div className="bg-kf-light border border-kf-primary-20 rounded p-3 mb-4 text-xs text-kf-primary-80">
              Ved å bekrefte bekrefter du at organisasjonen har nødvendig rettigheter til å bruke denne litteraturen i studieplanen. <strong>Ansvaret for at rettighetene faktisk foreligger ligger hos organisasjonen.</strong> Bekreftelsen lagres i systemet som audit-trail.
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setPendingAccept(null)}
                className="px-4 py-2 text-kf-primary hover:bg-kf-light rounded"
              >
                Avbryt
              </button>
              <button
                onClick={confirmAndAccept}
                className="bg-kf-accent text-kf-primary px-4 py-2 rounded hover:bg-kf-accent-80 font-medium"
              >
                Bekreft og legg til
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
